import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import PulseLoader from 'react-spinners/PulseLoader';
import { useHistory } from 'react-router-dom';
import {
  FiPlus,
  FiRefreshCw,
  FiExternalLink,
  FiTrash2,
  FiFolder,
  FiUser,
  FiActivity,
  FiFlag,
} from 'react-icons/fi';

import firebase from '../../../config/firebase.config';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { useTeamMemberDetailsForSelect } from '../../../hooks/useTeamMemberDetailsForSelect';
import { Text, textVariantsEnum } from '@/components/Text';
import { InputComponent } from '@/components/Input/Input.component';
import { Button as ButtonComponent, buttonVariants } from '@/components/Button';
import { PillDropdown } from '@/components/PillDropdown';
import { EmptyState } from '@/components/EmptyState';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { ActionsMenu } from '@/components/ActionsMenu';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { addNotification, notificationStates } from '@/components/Notification';
import { appRoutes } from '@/routes/routes';
import { tasksTableConfig } from '../Tasks.config';
import styles from '../Tasks.module.scss';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_PROJECTS,
  DEMO_MEMBER_DETAILS,
  DEMO_TASKS,
} from './ProdaffTasksTab.demoData';

const STATUS_OPTIONS = [
  { value: 'TO DO', label: 'TO DO' },
  { value: 'IN PROGRESS', label: 'IN PROGRESS' },
  { value: 'QA', label: 'QA' },
  { value: 'DONE', label: 'DONE' },
];

const PRIORITY_OPTIONS = [
  { value: 'P1', label: 'P1' },
  { value: 'P2', label: 'P2' },
  { value: 'P3', label: 'P3' },
  { value: 'P4', label: 'P4' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const database = firebase.firestore();

// ETA is stored as a zero-padded "DD/MM/YYYY" string (see task-form.jsx).
const parseEtaDate = (etaString) => {
  if (!etaString) return null;
  const [day, month, year] = etaString.split('/').map(Number);
  if (!day || !month || !year) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getEtaUrgency = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const overdueDays = Math.abs(diffDays);
    return {
      urgency: 'overdue',
      label: `Overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'}`,
    };
  }
  if (diffDays === 0) return { urgency: 'soon', label: 'Due today' };
  if (diffDays <= 3) {
    return {
      urgency: 'soon',
      label: `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`,
    };
  }
  return { urgency: 'normal', label: `${diffDays} days left` };
};

const getSortValue = (row, key, memberDetails) => {
  if (key === 'Assignee') {
    const member = memberDetails?.[row.Assignee];
    return (
      member?.userName ||
      member?.userEmailid ||
      row.Assignee ||
      ''
    ).toLowerCase();
  }
  if (key === 'ETA') {
    const date = parseEtaDate(row.ETA);
    return date ? date.getTime() : Number.POSITIVE_INFINITY;
  }
  if (key === 'id') return row.id || '';
  return (row[key] ?? '').toString().toLowerCase();
};

const ProdaffTasksTab = () => {
  const teams = useSelector((state) => ({
    loadingProject: state.team.loadingProject,
    activeTeam: state.team.activeTeam,
    selectedTeamId: state.team.selectedTeamID,
    projects: state.team.projects,
    active: state.team.active,
  }));

  const { activeCompany } = useSelector((state) => state.auth.profile);
  const dispatch = useDispatch();
  const history = useHistory();

  const activeTeamId =
    teams.selectedTeamId ?? teams.active?.activeTeamid ?? null;
  const { memberDetails, isLoading: membersLoading } =
    useTeamMemberDetailsForSelect(activeTeamId);

  // Demo mode: no real backend data exists for the demo profile, so
  // state.team.projects and useTeamMemberDetailsForSelect resolve empty —
  // override at the point of use so the project/assignee filters and task
  // table show a realistic dataset instead of "No Projects Found".
  const effectiveProjects = IS_DEMO_MODE ? DEMO_PROJECTS : teams.projects;
  const displayMemberDetails = IS_DEMO_MODE
    ? DEMO_MEMBER_DETAILS
    : memberDetails;
  const displayMembersLoading = IS_DEMO_MODE ? false : membersLoading;
  const displayLoadingProject = IS_DEMO_MODE ? false : teams.loadingProject;

  const members = useMemo(() => {
    if (!displayMemberDetails || typeof displayMemberDetails !== 'object')
      return [];
    return Object.entries(displayMemberDetails)
      .map(([uid, m]) => ({
        userUID: uid,
        userName: m?.userName || m?.userEmailid || uid,
      }))
      .filter((m) => m.userUID)
      .sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
  }, [displayMemberDetails]);

  const [taskLists, setTaskLists] = useState([]);
  const [initialTaskLists, setInitialTaskLists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [dataStatus, setDataStatus] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const menuRef = useRef(null);
  const [activeMenuRow, setActiveMenuRow] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isInitialDataLoaded =
    !displayLoadingProject &&
    !displayMembersLoading &&
    (!loading || projects.length === 0);

  useEffect(() => {
    if (IS_DEMO_MODE) return;
    if (!isEmpty(teams.active)) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany.companyId },
      });
    }
  }, [teams.selectedTeamId]);

  useEffect(() => {
    if (JSON.stringify(effectiveProjects) !== '{}' && members?.length) {
      const availableProjects = Array.isArray(effectiveProjects)
        ? effectiveProjects
            .filter((p) => !p?.data?.deleted)
            .map((p) => ({ id: p.id, data: p.data }))
        : [];

      setProjects(availableProjects);

      if (
        availableProjects.length > 0 &&
        members?.length &&
        selectedProjects.length === 0
      ) {
        const first = availableProjects[0];
        setSelectedProjects([
          { value: first.id, label: first.data.project_name },
        ]);
        fetchTasksFromProjects([first.id]);
      }
    }
  }, [members, effectiveProjects]);

  const refreshDataTable = async () => {
    const projectIds =
      selectedProjects.length > 0
        ? selectedProjects.map((p) => p.value)
        : projects.length > 0
          ? [projects[0].id]
          : [];

    if (projectIds.length > 0) await fetchTasksFromProjects(projectIds);
  };

  const navigate = async (data) => {
    // Demo mode: no real `companyDatabase/.../projects/{ProjectID}` doc
    // exists for the demo profile's project ids, so this lookup would throw
    // on `.data().project_name`. Resolve the project name locally instead.
    if (IS_DEMO_MODE) {
      const project = effectiveProjects.find((p) => p.id === data.ProjectID);
      const projectName = (project?.data?.project_name || 'project')
        .split(' ')
        .join('-');
      const newWindow = window.open(`task/${projectName}/${data.id}`, '_blank');
      if (newWindow) newWindow['projectId'] = data.ProjectID;
      return;
    }
    const projectData = await database
      .collection('companyDatabase')
      .doc(activeCompany.companyId)
      .collection('projects')
      .doc(data.ProjectID)
      .get();
    const projectName = projectData.data().project_name.split(' ').join('-');
    const newWindow = window.open(`task/${projectName}/${data.id}`, '_blank');
    newWindow['projectId'] = data.ProjectID;
  };

  const filterTaskList = (taskList, filters) =>
    taskList.filter((item) =>
      Object.entries(filters).every(([key, values]) => {
        if (!values.length) return true;

        if (key === 'searchTerm') {
          return (
            item?.Description?.toLowerCase().includes(values.toLowerCase()) ||
            item?.Title?.toLowerCase().includes(values.toLowerCase())
          );
        }
        if (key === 'ProjectID') {
          return values.some((v) => item[key] === v?.value);
        }
        return values.some((v) => item[key] === v?.value);
      })
    );

  useEffect(() => {
    if (!Array.isArray(initialTaskLists)) {
      setTaskLists([]);
      return;
    }
    setTaskLists(
      filterTaskList(initialTaskLists, {
        searchTerm,
        Assignee: assigneeFilter,
        Status: statusFilter,
        Priority: priorityFilter,
        ProjectID: selectedProjects,
      })
    );
  }, [
    assigneeFilter,
    statusFilter,
    priorityFilter,
    searchTerm,
    selectedProjects,
    dataStatus,
    initialTaskLists,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    assigneeFilter,
    statusFilter,
    priorityFilter,
    searchTerm,
    selectedProjects,
    sortedBy,
    reverseSort,
    pageSize,
  ]);

  const fetchTasksFromProjects = async (projectIds) => {
    if (!projectIds?.length) {
      setTaskLists([]);
      setInitialTaskLists([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Demo mode: no real task docs exist for the demo profile's project ids,
    // so the live Firestore query below would just resolve empty and clear
    // the list on every project-filter change. Filter the static demo
    // dataset locally instead — no live fetch involved.
    if (IS_DEMO_MODE) {
      const filtered = DEMO_TASKS.filter((task) =>
        projectIds.includes(task.ProjectID)
      );
      setInitialTaskLists(filtered);
      setLoading(false);
      setDataStatus((prev) => !prev);
      return;
    }

    try {
      const snapshot =
        projectIds.length === 1
          ? await database
              .collection('taskDatabase')
              .where('ProjectID', '==', projectIds[0])
              .get()
          : await database
              .collection('taskDatabase')
              .where('ProjectID', 'in', projectIds)
              .get();

      const listsWithId = snapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((task) => !task.Deleted);

      setInitialTaskLists(listsWithId);
    } catch (error) {
      console.error('[ProdaffTasksTab] fetch tasks error:', error);
      setTaskLists([]);
      setInitialTaskLists([]);
    } finally {
      setLoading(false);
      setDataStatus((prev) => !prev);
    }
  };

  const handleProjectChange = async (selected) => {
    setSelectedProjects(selected);
    if (selected.length === 0) {
      setTaskLists([]);
      setInitialTaskLists([]);
    } else {
      await fetchTasksFromProjects(selected.map((p) => p.value));
    }
  };

  const memberOptions = members.map((m) => ({
    value: m.userUID,
    label: m.userName,
  }));
  const projectOptions = Array.isArray(effectiveProjects)
    ? effectiveProjects
        .filter((p) => !p?.data?.deleted)
        .map((p) => ({ value: p.id, label: p.data.project_name }))
    : [];

  const toggleAddTask = useCallback(
    () => history.push(appRoutes().addTask),
    []
  );

  const sortedTaskLists = useMemo(() => {
    if (!sortedBy) return taskLists;
    const sorted = [...taskLists].sort((a, b) => {
      const av = getSortValue(a, sortedBy, displayMemberDetails);
      const bv = getSortValue(b, sortedBy, displayMemberDetails);
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    return reverseSort ? sorted.reverse() : sorted;
  }, [taskLists, sortedBy, reverseSort, displayMemberDetails]);

  const totalPages = Math.max(1, Math.ceil(sortedTaskLists.length / pageSize));
  const pagedTaskLists = sortedTaskLists.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const handleCopyTaskId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      addNotification({
        message: 'Task ID copied to clipboard',
        level: notificationStates.success,
      });
    } catch (error) {
      addNotification({
        message: 'Could not copy task ID',
        level: notificationStates.error,
      });
    }
  };

  const onActionsButtonClick = (e, row) => {
    setActiveMenuRow(row);
    menuRef.current.toggle(e);
  };

  const actionsMenuItems = useMemo(() => {
    if (!activeMenuRow) return [];
    return [
      {
        label: 'Open task',
        icon: <FiExternalLink size={16} />,
        onClick: () => {
          navigate(activeMenuRow);
          menuRef.current?.hide();
        },
      },
      {
        label: 'Delete task',
        icon: <FiTrash2 size={16} />,
        danger: true,
        onClick: () => {
          setDeleteRequest(activeMenuRow);
          menuRef.current?.hide();
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenuRow]);

  const handleDeleteTask = async () => {
    if (!deleteRequest || isDeleting) return;
    setIsDeleting(true);

    // Demo mode: no real task/project docs exist for the demo profile, so
    // the Firestore transaction below would fail — remove the task from
    // local state instead, mirroring what a successful delete would do.
    if (IS_DEMO_MODE) {
      setInitialTaskLists((prev) =>
        prev.filter((task) => task.id !== deleteRequest.id)
      );
      addNotification({
        message: 'Task deleted successfully',
        level: notificationStates.success,
      });
      setDeleteRequest(null);
      setIsDeleting(false);
      return;
    }

    try {
      await database.runTransaction(async (transaction) => {
        const taskRef = database
          .collection('taskDatabase')
          .doc(deleteRequest.id);
        const projectRef = database
          .collection('companyDatabase')
          .doc(activeCompany.companyId)
          .collection('projects')
          .doc(deleteRequest.ProjectID);
        transaction.update(taskRef, { Deleted: true });
        transaction.update(projectRef, {
          taskList: firebase.firestore.FieldValue.arrayRemove(deleteRequest.id),
        });
      });
      addNotification({
        message: 'Task deleted successfully',
        level: notificationStates.success,
      });
      setDeleteRequest(null);
      refreshDataTable();
    } catch (error) {
      console.error('[ProdaffTasksTab] delete task error:', error);
      addNotification({
        message: 'Task deletion failed',
        level: notificationStates.error,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = tasksTableConfig({
    memberDetails: displayMemberDetails,
    formatEtaDate: parseEtaDate,
    getEtaUrgency,
    onCopyId: handleCopyTaskId,
    onActionsButtonClick,
  });

  if (!isInitialDataLoaded) {
    return (
      <div
        className="w-100 d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <div className="text-center">
          <PulseLoader color="#4f58bb" size={15} />
          <div className="mt-3 text-muted">Loading...</div>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className={`text-center ${styles.emptyProjectsWrap}`}>
        <EmptyState
          illustration
          title="No Projects Found"
          description="Create a project first to start adding tasks."
          action={
            <ButtonComponent
              text="Create Project"
              onClick={() => history.push('/project')}
              variant={buttonVariants.primary}
            />
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.prodaffTasksRoot}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarRow}>
          <PillDropdown
            isMulti
            icon={<FiFolder size={15} />}
            label="Project"
            placeholder="All Projects"
            options={projectOptions}
            value={selectedProjects}
            onChange={handleProjectChange}
          />
          <PillDropdown
            isMulti
            icon={<FiUser size={15} />}
            label="Assignee"
            placeholder="All Assignees"
            options={memberOptions}
            value={assigneeFilter}
            onChange={setAssigneeFilter}
          />
          <PillDropdown
            isMulti
            icon={<FiActivity size={15} />}
            label="Status"
            placeholder="All Statuses"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <PillDropdown
            isMulti
            icon={<FiFlag size={15} />}
            label="Priority"
            placeholder="All Priorities"
            options={PRIORITY_OPTIONS}
            value={priorityFilter}
            onChange={setPriorityFilter}
          />
          <div className={styles.toolbarSpacer} />
          <ButtonComponent
            text="Add Task"
            icon={<FiPlus size={16} />}
            onClick={toggleAddTask}
            variant={buttonVariants.primary}
          />
        </div>
        <div className={styles.toolbarRow}>
          <InputComponent
            placeholder="Search tasks by title, ID or assignee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <div className={styles.toolbarSpacer} />
          <ButtonComponent
            icon={<FiRefreshCw size={14} />}
            variant={buttonVariants.outline}
            ariaLabel="Refresh task list"
            onClick={refreshDataTable}
          />
        </div>
      </div>

      <div className={styles.tableCard}>
        {loading ? (
          <div className="w-100 d-flex justify-content-center p-4">
            <PulseLoader color="#4f58bb" />
          </div>
        ) : selectedProjects.length === 0 ? (
          <div className="text-center p-4 text-muted">
            Please select a project to view tasks.
          </div>
        ) : taskLists.length === 0 ? (
          <div className={`text-center p-4 ${styles.emptyTasksWrap}`}>
            <EmptyState
              illustration
              title="No Tasks Found"
              description="No tasks found for the selected projects and filters."
              action={
                <ButtonComponent
                  text="Add Task"
                  onClick={toggleAddTask}
                  variant={buttonVariants.primary}
                />
              }
            />
          </div>
        ) : (
          <>
            <Table
              className={styles.tasksTable}
              data={pagedTaskLists}
              columns={columns}
              onSortClick={handleSortClick}
              sortedBy={sortedBy}
              reverseSort={reverseSort}
              onRowClick={navigate}
            />
            <div className={styles.tableFooter}>
              <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                Showing {(currentPage - 1) * pageSize + 1} to{' '}
                {Math.min(currentPage * pageSize, sortedTaskLists.length)} of{' '}
                {sortedTaskLists.length} tasks
              </Text>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageSizeChange={setPageSize}
              />
            </div>
          </>
        )}
      </div>

      <ActionsMenu items={actionsMenuItems} ref={menuRef} />
      <ConfirmActionDialog
        visible={Boolean(deleteRequest)}
        onHide={() => setDeleteRequest(null)}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDeleteTask}
        loading={isDeleting}
      />
    </div>
  );
};

export default ProdaffTasksTab;
